import classnames from 'classnames'
import { css } from 'emotion'
import * as React from 'react'

import Parent from 'common/services/Parent'

import { Offer } from 'embed/constants/models'
import { usePrevious, useTheme } from 'embed/hooks'

const ChevronRight = require('embed/components/icons/chevron-right.svg')

const styles = require('./styles.scss')

export interface RenderProps {
  offersPage?: Offer[]
  paginationControls: React.ReactNode
}

export interface PaginateOffersProps {
  children: ({ offersPage, paginationControls }: RenderProps) => React.ReactElement
  enabled: boolean
  offers: Offer[]
  offerListRef?: React.RefObject<HTMLOListElement>
  pageSize?: number
}

export interface PaginateOffersState {
  cursor: number
}

const PaginateOffers: React.FC<PaginateOffersProps> = ({ children, enabled, offers = [], offerListRef, pageSize }) => {
  const [cursor, setCursor] = React.useState(0)
  const { borderRadiusSmall, colorBorder, colorPrimary } = useTheme()
  const previousOffers = usePrevious(offers)

  React.useEffect(() => {
    if (previousOffers?.length !== offers.length) {
      setCursor(0)
    }
  }, [offers])

  const next = React.useCallback(() => {
    if (pageSize === undefined) {
      return
    }
    if (offerListRef) {
      Parent.scrollToFrame(offerListRef.current?.offsetTop)
    }
    setCursor(Math.min(cursor + pageSize, offers.length))
  }, [cursor, offerListRef, offers.length, pageSize])

  const prev = React.useCallback(() => {
    if (pageSize === undefined) {
      return
    }
    if (offerListRef) {
      Parent.scrollToFrame(offerListRef.current?.offsetTop)
    }
    setCursor(Math.max(cursor - pageSize, 0))
  }, [cursor, offerListRef, pageSize])

  const renderPaginationControls = () => {
    if (pageSize === undefined) {
      return null
    }

    const totalPages = Math.ceil(offers.length / pageSize)
    const currentPage = cursor / pageSize + 1

    const prevEnabled = currentPage > 1
    const nextEnabled = currentPage < totalPages

    const themedButtonClass = classnames(
      css`
        border-color: ${colorBorder};

        &:disabled path {
          fill: ${colorBorder};
        }
      `,
    )

    return (
      <div className={styles.container}>
        <div
          className={classnames(
            styles.controls,
            css`
              background-color: transparent};
              border-color: ${colorBorder};
              border-radius: ${borderRadiusSmall};
              color: ${colorPrimary};

              path {
                fill: ${colorPrimary};
              }
            `,
          )}
        >
          <button
            className={classnames(styles.button, themedButtonClass, styles.prev)}
            onClick={prev}
            disabled={!prevEnabled}
          >
            <ChevronRight className={styles.icon} />
          </button>
          <span>
            {currentPage} of {totalPages}
          </span>
          <button className={classnames(styles.button, themedButtonClass)} onClick={next} disabled={!nextEnabled}>
            <ChevronRight className={styles.icon} />
          </button>
        </div>
      </div>
    )
  }

  if (enabled && pageSize !== undefined && offers.length > pageSize) {
    const offersPage = offers.slice(cursor, cursor + pageSize)
    return children({ offersPage, paginationControls: renderPaginationControls() })
  } else {
    return children({ offersPage: offers, paginationControls: null })
  }
}

export default PaginateOffers
